import axios from "axios";
import httpClient from "./HttpClient";

export const PDF_READY_POLL_INTERVAL_MS = 5000;
export const PDF_READY_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Fetches the document JSON and reports whether its PDF rendition is ready.
 *
 * The backend generates a PDF rendition for every non-PDF document after
 * upload. `pdf_ready` is true when the original is a PDF or the rendition
 * exists, and false while conversion is still pending (see
 * docs/backend-api/documents.md, "Download Document File").
 *
 * @param {string|number} documentId - Document (or version) ID.
 * @returns {Promise<boolean>} Resolves true when the rendition is ready.
 */
const fetchPdfReady = function (documentId) {
  return httpClient
    .get("/documents/" + documentId + ".json")
    .then(function (response) {
      return !!(response.data && response.data.pdf_ready);
    });
};

/**
 * Probes a file.pdf URL to check rendition readiness without the document
 * JSON. The backend returns 202 Accepted (empty body) while the rendition is
 * pending, and 200/206 once it exists. The Range header keeps the transfer
 * small on servers that honor it.
 *
 * @param {string} fileUrl - Absolute file.pdf URL including the token param.
 * @returns {Promise<boolean>} Resolves true when the rendition is ready.
 */
const probePdfFileUrl = function (fileUrl) {
  return axios
    .get(fileUrl, {
      responseType: "arraybuffer",
      headers: { Range: "bytes=0-0" },
    })
    .then(function (response) {
      return response.status !== 202;
    });
};

/**
 * Waits until the backend's PDF rendition for a document is ready.
 *
 * The initial check prefers the preloaded document JSON when its id matches
 * (`options.document.pdf_ready`), then falls back to probing the file.pdf
 * URL (202 = pending), then to fetching the document JSON. While not ready,
 * the document JSON is re-fetched every PDF_READY_POLL_INTERVAL_MS. Polling
 * stops after PDF_READY_TIMEOUT_MS and reports failure via onTimeout.
 *
 * Network errors during a poll are treated as "not ready yet" and retried on
 * the next interval.
 *
 * @param {Object} options
 * @param {string|number} options.documentId - Document (or version) ID to poll.
 * @param {Object} [options.document] - Preloaded document JSON; used for the
 *   initial check when it carries a boolean `pdf_ready`.
 * @param {string} [options.fileUrl] - file.pdf URL used for the initial check
 *   when no usable document JSON is provided.
 * @param {Function} options.onReady - Called once when the rendition is ready.
 * @param {Function} [options.onTimeout] - Called once if the rendition is
 *   still pending after PDF_READY_TIMEOUT_MS.
 * @returns {Function} Cancel function. Clears the pending timer and
 *   guarantees neither callback fires afterwards. Call it on unmount and
 *   before starting a wait for a different document.
 */
export const waitForPdfReady = function (options) {
  const documentId = options.documentId;
  const preloadedDocument = options.document;
  const startedAt = Date.now();

  let settled = false;
  let timerId = null;

  const cancel = function () {
    settled = true;
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  };

  const finishReady = function () {
    if (settled) return;
    settled = true;
    options.onReady();
  };

  const finishTimedOut = function () {
    if (settled) return;
    settled = true;
    if (options.onTimeout) {
      options.onTimeout();
    }
  };

  const scheduleNextPoll = function () {
    if (settled) return;

    const hasTimedOut = Date.now() - startedAt >= PDF_READY_TIMEOUT_MS;
    if (hasTimedOut) {
      finishTimedOut();
      return;
    }

    timerId = setTimeout(pollDocumentJson, PDF_READY_POLL_INTERVAL_MS);
  };

  const handleResult = function (isReady) {
    if (settled) return;

    if (isReady) {
      finishReady();
    } else {
      scheduleNextPoll();
    }
  };

  const handleCheckError = function () {
    scheduleNextPoll();
  };

  const pollDocumentJson = function () {
    timerId = null;
    if (settled) return;
    fetchPdfReady(documentId).then(handleResult, handleCheckError);
  };

  const hasUsableDocument =
    preloadedDocument && typeof preloadedDocument.pdf_ready === "boolean";

  if (hasUsableDocument) {
    handleResult(preloadedDocument.pdf_ready === true);
  } else if (options.fileUrl) {
    probePdfFileUrl(options.fileUrl).then(handleResult, handleCheckError);
  } else {
    pollDocumentJson();
  }

  return cancel;
};
